// 写作页的写作教练（点「教练」或选中一段点「问教练」才拉出来）：对话（流式）、给我一个提示、知识参考、正在参考：当前文章 / 已选段落。
// 改写选段在正文的选区工具条上，检查全文在「完成」时；这里只管聊。每次调用前先把草稿存上，带着最新的版本号去问。

import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { App as AntApp } from 'antd'
import { ArrowUp, FileText, Lightbulb, Square, X } from 'lucide-react'
import { coach, type ChatMessage, type ContextRef, type Draft } from '../../api/write'
import { IconButton, Pill } from '../../ui'
import { AttachPicker } from '../kb/AttachPicker'
import { docTitle } from '../kb/DocCard'
import { useDocPreview } from '../kb/usePreview'
import { MicButton, useDictation } from '../voice/VoiceInput'

export interface Selection {
  from: number
  to: number
  text: string
}

interface Props {
  draft: Draft
  selection: Selection | null
  refs: ContextRef[]
  onRefs: (refs: ContextRef[]) => void
  /** 先存上，返回最新的版本号。 */
  flush: () => Promise<number>
  onClose: () => void
  /** 变了就把光标放进输入框（选中一段点「问教练」时）。 */
  focusKey?: number
  disabled?: boolean
}

const MAX_REFS = 6

export function Coach({ draft, selection, refs, onRefs, flush, onClose, focusKey, disabled }: Props) {
  const { message } = AntApp.useApp()
  const { open } = useDocPreview()
  const [messages, setMessages] = useState<ChatMessage[]>(draft.conversation)
  const [streaming, setStreaming] = useState<{ ask: string; text: string } | null>(null)
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const abort = useRef<AbortController | null>(null)
  const end = useRef<HTMLDivElement>(null)
  const box = useRef<HTMLTextAreaElement>(null)
  const voice = useDictation({ value: input, onChange: setInput, inputRef: box })
  const say = async () => void send('ask', voice.active ? await voice.stop() : input)

  useEffect(() => () => abort.current?.abort(), [])
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' })
  }, [messages.length, streaming?.text])
  useEffect(() => {
    if (focusKey) box.current?.focus()
  }, [focusKey])

  const send = async (mode: 'ask' | 'hint', text?: string) => {
    const ask = mode === 'hint' ? '给我一个提示' : (text ?? '').trim()
    if (!ask || busy) return
    setBusy(true)
    const ac = new AbortController()
    abort.current = ac
    try {
      const revision = await flush()
      setStreaming({ ask, text: '' })
      if (mode === 'ask') setInput('')
      const sel = selection ? { from: selection.from, to: selection.to } : undefined
      for await (const ev of coach(draft.id, { revision, mode, ...(mode === 'ask' ? { message: ask } : {}), ...(sel ? { selection: sel } : {}) }, ac.signal)) {
        if (ev.type === 'delta') setStreaming((s) => (s ? { ...s, text: s.text + ev.text } : s))
        if (ev.type === 'done') setMessages(ev.conversation)
        if (ev.type === 'error') throw new Error(ev.error)
      }
    } catch (e) {
      if (!ac.signal.aborted) {
        message.error(e instanceof Error ? e.message : '写作教练出错了')
        if (mode === 'ask') setInput((v) => v || ask)
      }
    } finally {
      setStreaming(null)
      setBusy(false)
      abort.current = null
    }
  }

  const words = selection ? (selection.text.match(/[\p{L}\p{N}]+/gu) ?? []).length : 0
  const off = disabled || busy

  return (
    <aside className="wc" aria-label="写作教练">
      <header className="wc-head">
        <h2>教练</h2>
        <span className="grow" />
        <Pill size="sm" variant="ghost" icon={<Lightbulb />} onClick={() => void send('hint')} disabled={off}>
          给我一个提示
        </Pill>
        <IconButton label="关上教练" onClick={onClose}>
          <X />
        </IconButton>
      </header>

      <div className="wc-thread" role="log" aria-label="和教练的对话">
        {!messages.length && !streaming && <p className="wc-rest">从一句不确定的话开始。教练先帮你想清楚，再给少量英文表达。</p>}
        {messages.map((m, i) => (
          <Message key={`${m.at}-${i}`} m={m} />
        ))}
        {streaming && (
          <>
            <Message m={{ role: 'user', text: streaming.ask, at: '' }} />
            <div className="wc-msg" data-role="assistant" aria-busy="true">
              {streaming.text ? <Markdown text={streaming.text} /> : <span className="live" role="status">教练在看你的文章…</span>}
            </div>
          </>
        )}
        <div ref={end} />
      </div>

      <div className="wc-foot">
        <div className="wc-context">
          <span>
            正在参考：<b>{selection ? `已选段落（${words} 词）` : '当前文章'}</b>
          </span>
          <span className="grow" />
          <AttachPicker picked={refs.map((r) => ({ id: r.knowledgeId }))} max={MAX_REFS} label="添加知识参考" placement="topRight" onPick={(k) => onRefs([...refs, { knowledgeId: k.id, title: docTitle(k) }])} />
        </div>
        {refs.length > 0 && (
          <div className="attach-row" aria-label="知识参考">
            {refs.map((r) => (
              <span key={r.knowledgeId} className="attach-chip">
                <FileText width={13} height={13} />
                <button type="button" className="wc-ref" title={r.title} onClick={() => open(r.knowledgeId)}>
                  {r.title}
                </button>
                <IconButton label={`不参考「${r.title}」`} onClick={() => onRefs(refs.filter((x) => x.knowledgeId !== r.knowledgeId))}>
                  <X width={12} height={12} />
                </IconButton>
              </span>
            ))}
          </div>
        )}
        <div className="oneline wc-input">
          <label className="sr-only" htmlFor="coach-input">
            和教练说
          </label>
          <textarea
            id="coach-input"
            ref={box}
            rows={1}
            value={input}
            placeholder={selection ? '这一段，你想说清楚什么？' : '和教练说…'}
            onChange={(e) => setInput(e.target.value)}
            readOnly={voice.active}
            data-dictating={voice.active}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault()
                void say()
              }
            }}
          />
          <MicButton d={voice} />
          {streaming ? (
            <IconButton label="停下" onClick={() => abort.current?.abort()}>
              <Square width={14} height={14} />
            </IconButton>
          ) : (
            <IconButton label="发给教练" variant="primary" onClick={() => void say()} disabled={off || (!input.trim() && !voice.active)}>
              <ArrowUp strokeWidth={2.2} />
            </IconButton>
          )}
        </div>
      </div>
    </aside>
  )
}

function Markdown({ text }: { text: string }) {
  return (
    <div className="answer">
      <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml>
        {text}
      </ReactMarkdown>
    </div>
  )
}

function Message({ m }: { m: ChatMessage }) {
  return (
    <div className="wc-msg" data-role={m.role}>
      <span className="wc-who">{m.role === 'user' ? '你' : '教练'}</span>
      {m.role === 'user' ? <p>{m.text}</p> : <Markdown text={m.text} />}
    </div>
  )
}
